import { IDropdownOption } from '@fluentui/react';
import { IH2OTheme, IItemStyles } from '@h2oai/ui-kit';

import { tagStyle } from '../../common/common.styles';

export type H2OGPTeEntry = {
  date: string;
  username: string;
  resource: 'Chat' | 'API Called' | 'RPC Called' | 'Collection' | 'Job' | 'Document' | 'Other' | 'N/A';
  action: string;
  count: number;
};

export enum H2OGPTeColumns {
  Date = 'date',
  Username = 'username',
  Resource = 'resource',
  Action = 'action',
  Count = 'count',
}

export const chatStyles = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    backgroundColor: theme.palette?.green100,
    color: theme.semanticColors?.categoryText,
  },
});

export const apiCalledStyles = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    backgroundColor: theme.palette?.blue200,
    color: theme.semanticColors?.categoryText,
  },
});

export const rpcCalledStyles = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    backgroundColor: theme.palette?.red100,
    color: theme.semanticColors?.categoryText,
  },
});

export const jobStyles = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    backgroundColor: theme.palette?.yellow200,
    color: theme.semanticColors?.categoryText,
  },
});

export const collectionStyles = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    backgroundColor: theme.palette?.blue400,
    color: theme.semanticColors?.categoryText,
  },
});

export const documentStyles = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    backgroundColor: theme.palette?.green200,
    color: theme.semanticColors?.categoryText,
  },
});

export const otherStyles = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    backgroundColor: theme.palette?.yellow100,
    color: theme.semanticColors?.categoryText,
  },
});

export const naStyles = (theme: IH2OTheme): Partial<IItemStyles> => ({
  root: {
    ...tagStyle(theme),
    backgroundColor: theme.palette?.gray300,
    color: theme.semanticColors?.categoryText,
  },
});

export const getGPTeResourceDropdownOptions = (): IDropdownOption[] => {
  return resourceOptions;
};

export const getGPTeActionsDropdownOptions = (selectedResource: string | null): IDropdownOption[] => {
  if (!selectedResource || selectedResource === 'all' || selectedResource === 'N/A') {
    return [
      { text: 'All', key: 'all' },
      ...chatActionsOptions,
      ...apiCalledActionsOptions,
      ...rpcCalledActionsOptions,
      ...documentActionsOptions,
      ...collectionActionsOptions,
      ...jobActionsOptions,
      ...otherActionsOptions,
      { text: 'N/A', key: 'N/A' },
    ];
  }
  if (selectedResource === 'Chat') {
    return [{ text: 'All', key: 'all' }, ...chatActionsOptions];
  }
  if (selectedResource === 'API Called') {
    return [{ text: 'All', key: 'all' }, ...apiCalledActionsOptions];
  }
  if (selectedResource === 'RPC Called') {
    return [{ text: 'All', key: 'all' }, ...rpcCalledActionsOptions];
  }
  if (selectedResource === 'Collection') {
    return [{ text: 'All', key: 'all' }, ...collectionActionsOptions];
  }
  if (selectedResource === 'Document') {
    return [{ text: 'All', key: 'all' }, ...documentActionsOptions];
  }
  if (selectedResource === 'Job') {
    return [{ text: 'All', key: 'all' }, ...jobActionsOptions];
  }
  if (selectedResource === 'Other') {
    return [{ text: 'All', key: 'all' }, ...otherActionsOptions];
  }

  return [];
};

const resourceOptions: IDropdownOption[] = [
  { text: 'All', key: 'all' },
  { text: 'Chat', key: 'Chat' },
  { text: 'API Called', key: 'API Called' },
  { text: 'RPC Called', key: 'RPC Called' },
  { text: 'Collection', key: 'Collection' },
  { text: 'Document', key: 'Document' },
  { text: 'Job', key: 'Job' },
  { text: 'Other', key: 'Other' },
  { text: 'N/A', key: 'N/A' },
];
const chatActionsOptions: IDropdownOption[] = [
  { text: 'Session Created', key: 'chatSessionCreated' },
  { text: 'Joined', key: 'chatjoined' },
  { text: 'Left', key: 'chatleft' },
  { text: 'Dropped', key: 'chatdropped' },
  { text: 'Message Received', key: 'chatmessagereceived' },
  { text: 'Message Created', key: 'chatmessagecreated' },
  { text: 'Messages Deleted', key: 'chatmessagesdeleted' },
  { text: 'Session Deleted', key: 'chatsessiondeleted' },
];

const apiCalledActionsOptions: IDropdownOption[] = [
  { text: 'Add agent key', key: 'add_agent_key' },
  { text: 'Add custom tools', key: 'add_custom_tools' },
  { text: 'Answer question using context', key: 'answer_question_using_context' },
  { text: 'Assign agent key for tool', key: 'assign_agent_key_for_tool' },
  { text: 'Create summary from context', key: 'create_summary_from_context' },
  { text: 'Delete agent custom tool', key: 'delete_agent_custom_tool' },
  { text: 'Delete agent server files', key: 'delete_agent_server_files' },
  { text: 'Delete multiple agent directories', key: 'delete_multiple_agent_directories' },
  { text: 'Encode for retrieval', key: 'encode_for_retrieval' },
  { text: 'Extract data from context', key: 'extract_data_from_context' },
  { text: 'Is self reflection allowed', key: 'is_self_reflection_allowed' },
  { text: 'Selftest', key: 'selftest' },
  { text: 'Selftest sync', key: 'selftest_sync' },
  { text: 'Update agent key', key: 'update_agent_key' },
  { text: 'Validate regex', key: 'validate_regex' },
];

const rpcCalledActionsOptions: IDropdownOption[] = [
  { text: 'Add Role To Group', key: 'add_role_to_group' },
  { text: 'Assign permissions to role', key: 'assign_permissions_to_role' },
  { text: 'Bad function', key: 'bad_function' },
  { text: 'Create API key', key: 'create_api_key' },
  { text: 'Create API key for collection', key: 'create_api_key_for_collection' },
  { text: 'Create extractor', key: 'create_extractor' },
  { text: 'Create or update agent tool preference', key: 'create_or_update_agent_tool_preference' },
  { text: 'Create prompt template', key: 'create_prompt_template' },
  { text: 'Create tag', key: 'create_tag' },
  { text: 'Create user group', key: 'create_user_group' },
  { text: 'Create user role', key: 'create_user_role' },
  { text: 'Delete agent keys', key: 'delete_agent_keys' },
  { text: 'Delete agent tool associations', key: 'delete_agent_tool_associations' },
  { text: 'Delete API keys', key: 'delete_api_keys' },
  { text: 'Delete API keys by admin', key: 'delete_api_keys_by_admin' },
  { text: 'Delete chat messages', key: 'delete_chat_messages' },
  { text: 'Delete chat sessions', key: 'delete_chat_sessions' },
  { text: 'Delete document summaries', key: 'delete_document_summaries' },
  { text: 'Delete extractors', key: 'delete_extractors' },
  { text: 'Delete prompt templates', key: 'delete_prompt_templates' },
  { text: 'Delete user groups by IDs', key: 'delete_user_groups_by_ids' },
  { text: 'Delete user groups by Names', key: 'delete_user_groups_by_names' },
  { text: 'Delete user roles by IDs', key: 'delete_user_roles_by_ids' },
  { text: 'Is collection permission granted', key: 'is_collection_permission_granted' },
  { text: 'Make collection private', key: 'make_collection_private' },
  { text: 'Remove collection expiry date', key: 'remove_collection_expiry_date' },
  { text: 'Rename chat session', key: 'rename_chat_session' },
  { text: 'Reset and share group prompt', key: 'reset_and_share_group_prompt' },
  { text: 'Reset and share user prompt', key: 'reset_and_share_user_prompt' },
  { text: 'Reset chat session prompt template', key: 'reset_chat_session_prompt_template' },
  { text: 'Reset collection prompt settings', key: 'reset_collection_prompt_settings' },
  { text: 'Reset collection prompt template', key: 'reset_collection_prompt_template' },
  { text: 'Reset roles for group', key: 'reset_roles_for_group' },
  { text: 'Reset roles for user', key: 'reset_roles_for_user' },
  { text: 'Reset user configurations for user', key: 'reset_user_configurations_for_user' },
  { text: 'Set chat message votes', key: 'set_chat_message_votes' },
  { text: 'Set chat session collection', key: 'set_chat_session_collection' },
  { text: 'Set chat session prompt template', key: 'set_chat_session_prompt_template' },
  { text: 'Set collection chat settings', key: 'set_collection_chat_settings' },
  { text: 'Set collection expiry date', key: 'set_collection_expiry_date' },
  { text: 'Set collection inactivity interval', key: 'set_collection_inactivity_interval' },
  { text: 'Set collection prompt template', key: 'set_collection_prompt_template' },
  { text: 'Set global configuration', key: 'set_global_configuration' },
  { text: 'Set user configuration for user', key: 'set_user_configuration_for_user' },
  { text: 'Set user configurations for user', key: 'set_user_configurations_for_user' },
  { text: 'Share prompt', key: 'share_prompt' },
  { text: 'Tag document', key: 'tag_document' },
  { text: 'Unarchive collection', key: 'unarchive_collection' },
  { text: 'Unshare collection for all', key: 'unshare_collection_for_all' },
  { text: 'Unshare prompt', key: 'unshare_prompt' },
  { text: 'Unshare prompt for all', key: 'unshare_prompt_for_all' },
  { text: 'Untag document', key: 'untag_document' },
  { text: 'Update collection metadata', key: 'update_collection_metadata' },
  { text: 'Update collection prompt settings', key: 'update_collection_prompt_settings' },
  { text: 'Update collection RAG type', key: 'update_collection_rag_type' },
  { text: 'Update collection settings', key: 'update_collection_settings' },
  { text: 'Update document metadata', key: 'update_document_metadata' },
  { text: 'Update document name', key: 'update_document_name' },
  { text: 'Update document summary content', key: 'update_document_summary_content' },
  { text: 'Update document URI', key: 'update_document_uri' },
  { text: 'Update expected answer text', key: 'update_expected_answer_text' },
  { text: 'Update extractor', key: 'update_extractor' },
  { text: 'Update prompt template', key: 'update_prompt_template' },
  { text: 'Update tag', key: 'update_tag' },
  { text: 'Update user role description', key: 'update_user_role_description' },
];

const collectionActionsOptions: IDropdownOption[] = [
  { text: 'Collection Created', key: 'collectioncreated' },
  { text: 'Collection Deleted', key: 'collectiondeleted' },
  { text: 'Collection Deleted by Admin', key: 'collectiondeletedbyadmin' },
  { text: 'Collection Made Public', key: 'collectionmadepublic' },
  { text: 'Collection Made Private', key: 'collectionmadeprivate' },
  { text: 'Collection Updated', key: 'collectionupdated' },
  { text: 'Shared With User', key: 'collectionsharedwithuser' },
  { text: 'Collection Viewed', key: 'collectionviewed' },
  { text: 'Collection RAG Type Updated', key: 'collectionragtypeupdated' },
  { text: 'Collection Prompt Template Reset', key: 'collectionprompttemplatereset' },
  { text: 'Collection Prompt Template Set', key: 'collectionprompttemplateset' },
  { text: 'Collection Settings Updated', key: 'collectionsettingsupdated' },
  { text: 'Collection Chat Settings Set', key: 'collectionchatsettingsset' },
  { text: 'Collection Size Limit Set', key: 'collectionsizelimitset' },
  { text: 'Collection Size Limit Removed', key: 'collectionsizelimitremoved' },
  { text: 'Collection Expiry Date Set', key: 'collectionexpirydateset' },
  { text: 'Collection Expiry Date Removed', key: 'collectionexpirydateremoved' },
  { text: 'Collection Inactivity Interval Set', key: 'collectioninactivityintervalset' },
  { text: 'Collection Inactivity Interval Removed', key: 'collectioninactivityintervalremoved' },
  { text: 'Collection Recovered', key: 'collectionrecovered' },
];

const documentActionsOptions: IDropdownOption[] = [
  { text: 'Document Created', key: 'documentcreated' },
  { text: 'Document Deleted', key: 'documentdeleted' },
  { text: 'Document Uploaded', key: 'documentuploaded' },
  { text: 'Document Indexed', key: 'documentindexed' },
  { text: 'Document Viewed', key: 'documentviewed' },
  { text: 'Document Name Updated', key: 'documentnameupdated' },
  { text: 'Document URI Updated', key: 'documenturiupdated' },
];

const otherActionsOptions: IDropdownOption[] = [
  { text: 'API Key Created for User', key: 'apikeycreatedforuser' },
  { text: 'API Key Deleted for User', key: 'apikeydeletedforuser' },
  { text: 'API Key Expiry Set', key: 'apikeyexpiryset' },
  { text: 'API Key Deactivated', key: 'apikeydeactivated' },
  { text: 'User Roles Updated', key: 'userrolesupdated' },
  { text: 'Role Created', key: 'rolecreated' },
  { text: 'Role Permissions Updated', key: 'rolepermissionsupdated' },
  { text: 'Role Deleted', key: 'roledeleted' },
  { text: 'User Group Created', key: 'usergroupcreated' },
  { text: 'User Group Roles Reset', key: 'usergrouprolesreset' },
  { text: 'User Groups Deleted', key: 'usergroupsdeleted' },
  { text: 'User Deleted', key: 'userdeleted' },
  { text: 'Archived Collection Deleted', key: 'archivedcollectiondeleted' },
  { text: 'User Job Canceled', key: 'userjobcanceled' },
];

const jobActionsOptions: IDropdownOption[] = [{ text: 'Job Started', key: 'jobstarted' }];

export const gpteDailyTokenUsageLabelsMap: Record<string, string> = {
  inputTokens: 'Input Tokens',
  outputTokens: 'Output Tokens',
};

export const gpteDailyTokenUsageColorMap: Record<string, string> = {
  inputTokens: '#64B5F6',
  outputTokens: '#FF8FA3',
};

export const llmColorMap: Record<string, string> = {
  // Empty/NA
  '': '#D1D5DB',

  // Nous Research - Light purple/lavender shades
  'NousResearch/Nous-Capybara-34B': '#DDD6FE',
  'NousResearch/Nous-Capybara-34b': '#DDD6FE',
  'NousResearch/Nous-Hermes-2-Mixtral-8x7B-DPO': '#C4B5FD',

  // OpenGVLab - Light teal shades
  'OpenGVLab/InternVL-Chat-V1-5': '#CCFBF1',
  'OpenGVLab/InternVL2-Llama3-76B': '#B2F5EA',

  // Qwen - Light blue shades
  'Qwen/QwQ-32B': '#DBEAFE',
  'Qwen/Qwen1.5-72B-Chat': '#BFDBFE',
  'Qwen/Qwen2-72B-Instruct-GPTQ-Int4': '#A3CFFF',
  'Qwen/Qwen2-VL-72B-Instruct': '#93C5FD',
  'Qwen/Qwen2-VL-7B-Instruct': '#7DD3FC',
  'Qwen/Qwen2.5-72B-Instruct': '#BAE6FD',
  'Qwen/Qwen2.5-VL-72B-Instruct': '#A5F3FC',
  'Qwen/Qwen3-235B-A22B-FP8': '#67E8F9',

  // SeaLLMs - Light cyan
  'SeaLLMs/SeaLLM-7B-v2.5': '#A5F3FC',

  // Claude - Light peach/coral shades
  'claude-2.1': '#FED7AA',
  'claude-3-5-haiku-20241022': '#FECACA',
  'claude-3-5-haiku-20241022_1': '#FECACA',
  'claude-3-5-sonnet-20240620': '#FCA5A5',
  'claude-3-5-sonnet-20240620-bedrock': '#F87171',
  'claude-3-5-sonnet-20241022': '#FCA5A5',
  'claude-3-5-sonnet-20241022-bedrock': '#F87171',
  'claude-3-7-sonnet-20250219': '#FEE2E2',
  'claude-3-7-sonnet-20250219-reasoning': '#FECDD3',
  'claude-3-haiku-20240307': '#FECACA',
  'claude-3-haiku-20240307-bedrock': '#FED7D7',
  'claude-3-opus-20240229': '#FCA5A5',
  'claude-3-sonnet-20240229': '#F87171',
  'claude-opus-4-20250514': '#FEE2E2',
  'claude-sonnet-4-20250514': '#FECDD3',

  // DeepSeek - Light indigo/periwinkle shades
  'deepseek-ai/DeepSeek-R1': '#E0E7FF',
  'deepseek-ai/DeepSeek-R1-Distill-Qwen-32B': '#C7D2FE',
  'deepseek-ai/DeepSeek-R1-shadeform': '#A5B4FC',
  'deepseek-ai/DeepSeek-R1-together': '#8B95FF',
  'deepseek-ai/DeepSeek-V3-together': '#818CF8',
  'deepseek-ai/deepseek-llm-67b-chat': '#A78BFA',

  // Gemini - Light mint/sage green shades
  'gemini-1.5-flash-latest': '#D1FAE5',
  'gemini-1.5-pro-latest': '#BBF7D0',
  'gemini-2.0-flash': '#A7F3D0',
  'gemini-2.0-flash-thinking-exp-01-21': '#86EFAC',
  'gemini-2.5-flash-preview-04-17': '#6EE7B7',
  'gemini-2.5-flash-preview-04-17-reasoning': '#5EEAD4',
  'gemini-2.5-flash-preview-05-20': '#6EE7B7',
  'gemini-2.5-flash-preview-05-20-reasoning': '#5EEAD4',
  'gemini-2.5-pro-preview-03-25': '#A7F3D0',
  'gemini-2.5-pro-preview-03-25-reasoning': '#86EFAC',
  'gemini-2.5-pro-preview-05-06': '#A7F3D0',
  'gemini-2.5-pro-preview-05-06-reasoning': '#86EFAC',
  'gemini-pro': '#D1FAE5',

  // Google/Gemma - Light lime
  'google/gemma-2-27b-it': '#ECFCCB',

  // GPT - Light yellow/cream shades
  'gpt-35-turbo-1106': '#FEF3C7',
  'gpt-4-1106-preview': '#FDE68A',
  'gpt-4o': '#FCD34D',
  'gpt-4o-mini': '#FBBF24',

  // H2O.ai - Light pink/rose shades
  'h2oai/h2o-danube2-1.8b-chat': '#FCE7F3',
  'h2oai/h2o-danube3-4b-chat': '#FBCFE8',
  'h2oai/h2ogpt-4096-llama2-70b-chat': '#F9A8D4',
  'h2oai/h2ogpt-4096-llama2-70b-chat-4bit': '#F472B6',
  'h2oai/h2ovl-mississippi-2b': '#EC4899',
  'h2oai/mississippi-TD-finetuned': '#DB2777',

  // LLaVA - Light violet
  'liuhaotian/llava-v1.6-34b': '#E9D5FF',

  // Meta Llama - Light coral/salmon shades
  'meta-llama/Llama-3-70b-chat-hf': '#FEE2E2',
  'meta-llama/Llama-3-8b-chat-hf': '#FECACA',
  'meta-llama/Llama-3.1-70B-Instruct': '#FCA5A5',
  'meta-llama/Llama-3.2-11B-Vision-Instruct-Turbo': '#F87171',
  'meta-llama/Llama-3.2-3B-Instruct-Turbo': '#EF4444',
  'meta-llama/Llama-3.2-90B-Vision-Instruct-Turbo': '#DC2626',
  'meta-llama/Llama-3.3-70B-Instruct': '#FEE2E2',
  'meta-llama/Llama-4-Maverick-17B-128E-Instruct-FP8': '#FECDD3',
  'meta-llama/Llama-4-Scout-17B-16E-Instruct': '#FED7D7',
  'meta-llama/Llama-Guard-3-8B': '#FEE2E2',
  'meta-llama/Meta-Llama-3-70B-Instruct': '#FEE2E2',
  'meta-llama/Meta-Llama-3-8B': '#FECACA',
  'meta-llama/Meta-Llama-3-8B-Instruct': '#FCA5A5',
  'meta-llama/Meta-Llama-3.1-405B-Instruct': '#F87171',
  'meta-llama/Meta-Llama-3.1-405B-Instruct-FP8': '#EF4444',
  'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo': '#DC2626',
  'meta-llama/Meta-Llama-3.1-70B-Instruct': '#FEE2E2',
  'meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo': '#FECACA',
  'meta-llama/Meta-Llama-3.1-8B-Instruct': '#FCA5A5',
  'meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo': '#F87171',
  'meta-llama/Meta-Llama-3.1-8B-Instruct_1': '#FCA5A5',

  // Microsoft Phi - Light sky blue shades
  'microsoft/Phi-3-medium-128k-instruct': '#E0F2FE',
  'microsoft/Phi-3-mini-128k-instruct': '#BAE6FD',
  'microsoft/Phi-3-vision-128k-instruct': '#7DD3FC',
  'microsoft/Phi-3.5-mini-instruct': '#38BDF8',
  'microsoft/Phi-3.5-vision-instruct': '#0EA5E9',

  // Mistral - Light emerald/seafoam shades
  'mistral-large-latest': '#D1FAE5',
  'mistral-medium': '#BBF7D0',
  'mistral-medium-latest': '#A7F3D0',
  'mistral-small-latest': '#86EFAC',
  'mistral-tiny': '#6EE7B7',
  'mistralai/Mistral-7B-Instruct-v0.2': '#D1FAE5',
  'mistralai/Mistral-7B-Instruct-v0.3': '#BBF7D0',
  'mistralai/Mistral-7B-Instruct-v0.3_1': '#BBF7D0',
  'mistralai/Mistral-Nemo-Instruct-2407': '#A7F3D0',
  'mistralai/Mixtral-8x7B-Instruct-v0.1': '#86EFAC',
  'mistralai/Pixtral-12B-2409': '#6EE7B7',
  'mistralai/Pixtral-12B-2409_1': '#6EE7B7',

  // NVIDIA - Light lime green
  'nvidia/Llama-3.1-Nemotron-70B-Instruct-HF': '#ECFCCB',

  // OpenAI O-series - Light gold/champagne shades
  o1: '#FEF3C7',
  'o1-mini': '#FDE68A',
  'o3-mini': '#FCD34D',
  'o4-mini': '#FBBF24',

  // OpenChat - Light slate/lavender gray
  'openchat/openchat-3.5-1210': '#E2E8F0',

  // Perplexity - Light purple
  'perplexity-ai/r1-1776': '#DDD6FE',

  // Upstage Solar - Light amber
  'upstage/SOLAR-10.7B-Instruct-v1.0': '#FEF3C7',
};

export const descriptionsColorMap: Record<string, string> = {
  // Core data - Light gray
  all_data: '#F3F4F6',

  // Auto features - Light blue shades
  auto: '#DBEAFE',
  auto_rag: '#BFDBFE',

  // Chat features - Light green
  chat_session_rename: '#D1FAE5',

  // HyDE variants - Light purple shades
  hyde1: '#E9D5FF',
  hyde2: '#DDD6FE',

  // Core LLM - Light amber
  llm_only: '#FEF3C7',

  // PII features - Light pink/rose shades
  pii_model: '#FCE7F3',
  pii_presidio: '#FBCFE8',
  pii_regex: '#F9A8D4',

  // Security - Light orange
  prompt_guardrails: '#FED7AA',

  // RAG variants - Light teal shades
  rag: '#CCFBF1',
  'rag+': '#B2F5EA',

  // Reflection - Light violet
  self_reflection: '#E0E7FF',
};

export const gpteEventCountsLabelsMap: Record<string, string> = {
  chat: 'Chat',
  apiCalled: 'API Called',
  rpcCalled: 'RPC Called',
  collection: 'Collection',
  document: 'Document',
  other: 'Other',
  job: 'Job',
};

export const gpteEventCountsColorMap: Record<string, string> = {
  chat: '#FF8FA3',
  apiCalled: '#FFCC80',
  rpcCalled: '#81D4FA',
  collection: '#BCAAA4',
  document: '#9FA8DA',
  other: '#A5D6A7',
  job: '#80CBC4',
};

export const keyNameMap: Record<string, string> = {
  documentcreated: 'Created',
  documentdeleted: 'Deleted',
  documentuploaded: 'Uploaded',
  documentindexed: 'Indexed',
  documentviewed: 'Viewed',
  documentnameupdated: 'Name Updated',
  documenturiupdated: 'URI Updated',

  chatsessioncreated: 'Session Created',
  chatjoined: 'Joined',
  chatleft: 'Left',
  chatdropped: 'Dropped',
  chatmessagereceived: 'Message Received',
  chatmessagecreated: 'Message Created',
  chatmessagesdeleted: 'Messages Deleted',
  chatsessiondeleted: 'Session Deleted',

  collectioncreated: 'Created',
  collectiondeleted: 'Deleted',
  collectiondeletedbyadmin: 'Deleted by Admin',
  collectionmadepublic: 'Made Public',
  collectionmadeprivate: 'Made Private',
  collectionupdated: 'Updated',
  collectionsharedwithuser: 'Shared With User',
  collectionviewed: 'Viewed',
  collectionragtypeupdated: 'RAG Type Updated',
  collectionprompttemplatereset: 'Prompt Template Reset',
  collectionprompttemplateset: 'Prompt Template Set',
  collectionsettingsupdated: 'Settings Updated',
  collectionchatsettingsset: 'Chat Settings Set',
  collectionsizelimitset: 'Size Limit Set',
  collectionsizelimitremoved: 'Size Limit Removed',
  collectionexpirydateset: 'Expiry Date Set',
  collectionexpirydateremoved: 'Expiry Date Removed',
  collectioninactivityintervalset: 'Inactivity Interval Set',
  collectioninactivityintervalremoved: 'Inactivity Interval Removed',
  collectionrecovered: 'Recovered',

  jobstarted: 'Started',

  add_agent_key: 'Add agent key',
  add_custom_tools: 'Add custom tools',
  answer_question_using_context: 'Answer question using context',
  assign_agent_key_for_tool: 'Assign agent key for tool',
  create_summary_from_context: 'Create summary from context',
  delete_agent_custom_tool: 'Delete agent custom tool',
  delete_agent_server_files: 'Delete agent server files',
  delete_multiple_agent_directories: 'Delete multiple agent directories',
  encode_for_retrieval: 'Encode for retrieval',
  extract_data_from_context: 'Extract data from context',
  is_self_reflection_allowed: 'Is self reflection allowed',
  selftest: 'Selftest',
  selftest_sync: 'Selftest sync',
  update_agent_key: 'Update agent key',
  validate_regex: 'Validate regex',

  add_role_to_group: 'Add Role To Group',
  assign_permissions_to_role: 'Assign Permissions To Role',
  bad_function: 'Bad Function',
  create_api_key: 'Create API Key',
  create_api_key_for_collection: 'Create API Key For Collection',
  create_extractor: 'Create Extractor',
  create_or_update_agent_tool_preference: 'Create Or Update Agent Tool Preference',
  create_prompt_template: 'Create Prompt Template',
  create_tag: 'Create Tag',
  create_user_group: 'Create User Group',
  create_user_role: 'Create User Role',
  delete_agent_keys: 'Delete agent keys',
  delete_agent_tool_associations: 'Delete Agent Tool Associations',
  delete_api_keys: 'Delete API Keys',
  delete_api_keys_by_admin: 'Delete API Keys By Admin',
  delete_chat_messages: 'Delete Chat Messages',
  delete_chat_sessions: 'Delete Chat Sessions',
  delete_document_summaries: 'Delete Document Summaries',
  delete_extractors: 'Delete Extractors',
  delete_prompt_templates: 'Delete Prompt Templates',
  delete_user_groups_by_ids: 'Delete User Groups By IDs',
  delete_user_groups_by_names: 'Delete user Groups By Names',
  delete_user_roles_by_ids: 'Delete User Roles By IDs',
  is_collection_permission_granted: 'Is Collection Permission Granted',
  make_collection_private: 'Make Collection Private',
  remove_collection_expiry_date: 'Remove Collection Expiry Date',
  rename_chat_session: 'Rename Chat Session',
  reset_and_share_group_prompt: 'Reset and Share Group Prompt',
  reset_and_share_user_prompt: 'Reset and Share User Prompt',
  reset_chat_session_prompt_template: 'Reset Chat Session Prompt Template',
  reset_collection_prompt_settings: 'Reset Collection Prompt Settings',
  reset_collection_prompt_template: 'Reset Collection Prompt Template',
  reset_roles_for_group: 'Reset Roles For Group',
  reset_roles_for_user: 'Reset Roles For User',
  reset_user_configurations_for_user: 'Reset User Configurations',
  set_chat_message_votes: 'Set Chat Message Votes',
  set_chat_session_collection: 'Set Chat Session Collection',
  set_chat_session_prompt_template: 'Set Chat Session Prompt Template',
  set_collection_chat_settings: 'Set Collection Chat Settings',
  set_collection_expiry_date: 'Set Collection Expiry Date',
  set_collection_inactivity_interval: 'Set Collection Inactivity Interval',
  set_collection_prompt_template: 'Set Collection Prompt Template',
  set_global_configuration: 'Set Global Configuration',
  set_user_configuration_for_user: 'Set User Configuration',
  set_user_configurations_for_user: 'Set User Configurations',
  share_prompt: 'Share Prompt',
  tag_document: 'Tag Document',
  unarchive_collection: 'Unarchive Collection',
  unshare_collection_for_all: 'Unshare Collection For All',
  unshare_prompt: 'Unshare Prompt',
  unshare_prompt_for_all: 'Unshare Prompt For All',
  untag_document: 'Untag Document',
  update_collection_metadata: 'Update Collection Metadata',
  update_collection_prompt_settings: 'Update Collection Prompt Settings',
  update_collection_rag_type: 'Update Collection RAG Type',
  update_collection_settings: 'Update Collection Settings',
  update_document_metadata: 'Update Document Metadata',
  update_document_name: 'Update Document Name',
  update_document_summary_content: 'Update Document Summary Content',
  update_document_uri: 'Update Document URI',
  update_expected_answer_text: 'Update Expected Answer Text',
  update_extractor: 'Update Extractor',
  update_prompt_template: 'Update Prompt Template',
  update_tag: 'Update Tag',
  update_user_role_description: 'Update User Role Description',
  user_roles_updated: 'User Roles Updated',
  role_permissions_updated: 'Role Permissions Updated',
  user_group_created: 'User Group Created',
  user_group_roles_reset: 'User Group Roles Reset',
  user_deleted: 'User Deleted',

  '': 'Unknown',
  'NousResearch/Nous-Capybara-34B': 'Nous Capybara 34B',
  'NousResearch/Nous-Capybara-34b': 'Nous Capybara 34B',
  'NousResearch/Nous-Hermes-2-Mixtral-8x7B-DPO': 'Nous Hermes 2 Mixtral 8x7B DPO',
  'OpenGVLab/InternVL-Chat-V1-5': 'InternVL Chat V1 5',
  'OpenGVLab/InternVL2-Llama3-76B': 'InternVL2 Llama3 76B',
  'Qwen/QwQ-32B': 'Qwen QwQ 32B',
  'Qwen/Qwen1.5-72B-Chat': 'Qwen1.5 72B Chat',
  'Qwen/Qwen2-72B-Instruct-GPTQ-Int4': 'Qwen2 72B Instruct GPTQ Int4',
  'Qwen/Qwen2-VL-72B-Instruct': 'Qwen2 VL 72B Instruct',
  'Qwen/Qwen2-VL-7B-Instruct': 'Qwen2 VL 7B Instruct',
  'Qwen/Qwen2.5-72B-Instruct': 'Qwen2.5 72B Instruct',
  'Qwen/Qwen2.5-VL-72B-Instruct': 'Qwen2.5 VL 72B Instruct',
  'Qwen/Qwen3-235B-A22B-FP8': 'Qwen3 235B A22B FP8',
  'SeaLLMs/SeaLLM-7B-v2.5': 'SeaLLM 7B v2.5',
  'claude-2.1': 'Claude 2.1',
  'claude-3-5-haiku-20241022': 'Claude 3.5 Haiku (20241022)',
  'claude-3-5-haiku-20241022_1': 'Claude 3.5 Haiku (20241022)',
  'claude-3-5-sonnet-20240620': 'Claude 3.5 Sonnet (20240620)',
  'claude-3-5-sonnet-20240620-bedrock': 'Claude 3.5 Sonnet Bedrock',
  'claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet (20241022)',
  'claude-3-5-sonnet-20241022-bedrock': 'Claude 3.5 Sonnet Bedrock',
  'claude-3-7-sonnet-20250219': 'Claude 3.7 Sonnet (20250219)',
  'claude-3-7-sonnet-20250219-reasoning': 'Claude 3.7 Sonnet Reasoning',
  'claude-3-haiku-20240307': 'Claude 3 Haiku (20240307)',
  'claude-3-haiku-20240307-bedrock': 'Claude 3 Haiku Bedrock',
  'claude-3-opus-20240229': 'Claude 3 Opus (20240229)',
  'claude-3-sonnet-20240229': 'Claude 3 Sonnet (20240229)',
  'claude-opus-4-20250514': 'Claude Opus 4 (20250514)',
  'claude-sonnet-4-20250514': 'Claude Sonnet 4 (20250514)',
  'deepseek-ai/DeepSeek-R1': 'DeepSeek R1',
  'deepseek-ai/DeepSeek-R1-Distill-Qwen-32B': 'DeepSeek R1 Distill Qwen 32B',
  'deepseek-ai/DeepSeek-R1-shadeform': 'DeepSeek R1 Shadeform',
  'deepseek-ai/DeepSeek-R1-together': 'DeepSeek R1 Together',
  'deepseek-ai/DeepSeek-V3-together': 'DeepSeek V3 Together',
  'deepseek-ai/deepseek-llm-67b-chat': 'DeepSeek LLM 67B Chat',
  'gemini-1.5-flash-latest': 'Gemini 1.5 Flash Latest',
  'gemini-1.5-pro-latest': 'Gemini 1.5 Pro Latest',
  'gemini-2.0-flash': 'Gemini 2.0 Flash',
  'gemini-2.0-flash-thinking-exp-01-21': 'Gemini 2.0 Flash Thinking Exp',
  'gemini-2.5-flash-preview-04-17': 'Gemini 2.5 Flash Preview',
  'gemini-2.5-flash-preview-04-17-reasoning': 'Gemini 2.5 Flash Reasoning',
  'gemini-2.5-flash-preview-05-20': 'Gemini 2.5 Flash Preview',
  'gemini-2.5-flash-preview-05-20-reasoning': 'Gemini 2.5 Flash Reasoning',
  'gemini-2.5-pro-preview-03-25': 'Gemini 2.5 Pro Preview',
  'gemini-2.5-pro-preview-03-25-reasoning': 'Gemini 2.5 Pro Reasoning',
  'gemini-2.5-pro-preview-05-06': 'Gemini 2.5 Pro Preview',
  'gemini-2.5-pro-preview-05-06-reasoning': 'Gemini 2.5 Pro Reasoning',
  'gemini-pro': 'Gemini Pro',
  'google/gemma-2-27b-it': 'Gemma 2 27B IT',
  'gpt-35-turbo-1106': 'GPT 3.5 Turbo 1106',
  'gpt-4-1106-preview': 'GPT 4 1106 Preview',
  'gpt-4o': 'GPT 4o',
  'gpt-4o-mini': 'GPT 4o Mini',
  'h2oai/h2o-danube2-1.8b-chat': 'H2O Danube2 1.8B Chat',
  'h2oai/h2o-danube3-4b-chat': 'H2O Danube3 4B Chat',
  'h2oai/h2ogpt-4096-llama2-70b-chat': 'H2OGPT 4096 Llama2 70B Chat',
  'h2oai/h2ogpt-4096-llama2-70b-chat-4bit': 'H2OGPT 4096 Llama2 70B Chat 4bit',
  'h2oai/h2ovl-mississippi-2b': 'H2OVL Mississippi 2B',
  'h2oai/mississippi-TD-finetuned': 'Mississippi TD Finetuned',
  'liuhaotian/llava-v1.6-34b': 'LLaVA v1.6 34B',
  'meta-llama/Llama-3-70b-chat-hf': 'Llama 3 70B Chat HF',
  'meta-llama/Llama-3-8b-chat-hf': 'Llama 3 8B Chat HF',
  'meta-llama/Llama-3.1-70B-Instruct': 'Llama 3.1 70B Instruct',
  'meta-llama/Llama-3.2-11B-Vision-Instruct-Turbo': 'Llama 3.2 11B Vision Instruct Turbo',
  'meta-llama/Llama-3.2-3B-Instruct-Turbo': 'Llama 3.2 3B Instruct Turbo',
  'meta-llama/Llama-3.2-90B-Vision-Instruct-Turbo': 'Llama 3.2 90B Vision Instruct Turbo',
  'meta-llama/Llama-3.3-70B-Instruct': 'Llama 3.3 70B Instruct',
  'meta-llama/Llama-4-Maverick-17B-128E-Instruct-FP8': 'Llama 4 Maverick 17B 128E Instruct FP8',
  'meta-llama/Llama-4-Scout-17B-16E-Instruct': 'Llama 4 Scout 17B 16E Instruct',
  'meta-llama/Llama-Guard-3-8B': 'Llama Guard 3 8B',
  'meta-llama/Meta-Llama-3-70B-Instruct': 'Meta Llama 3 70B Instruct',
  'meta-llama/Meta-Llama-3-8B': 'Meta Llama 3 8B',
  'meta-llama/Meta-Llama-3-8B-Instruct': 'Meta Llama 3 8B Instruct',
  'meta-llama/Meta-Llama-3.1-405B-Instruct': 'Meta Llama 3.1 405B Instruct',
  'meta-llama/Meta-Llama-3.1-405B-Instruct-FP8': 'Meta Llama 3.1 405B Instruct FP8',
  'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo': 'Meta Llama 3.1 405B Instruct Turbo',
  'meta-llama/Meta-Llama-3.1-70B-Instruct': 'Meta Llama 3.1 70B Instruct',
  'meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo': 'Meta Llama 3.1 70B Instruct Turbo',
  'meta-llama/Meta-Llama-3.1-8B-Instruct': 'Meta Llama 3.1 8B Instruct',
  'meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo': 'Meta Llama 3.1 8B Instruct Turbo',
  'meta-llama/Meta-Llama-3.1-8B-Instruct_1': 'Meta Llama 3.1 8B Instruct',
  'microsoft/Phi-3-medium-128k-instruct': 'Phi 3 Medium 128K Instruct',
  'microsoft/Phi-3-mini-128k-instruct': 'Phi 3 Mini 128K Instruct',
  'microsoft/Phi-3-vision-128k-instruct': 'Phi 3 Vision 128K Instruct',
  'microsoft/Phi-3.5-mini-instruct': 'Phi 3.5 Mini Instruct',
  'microsoft/Phi-3.5-vision-instruct': 'Phi 3.5 Vision Instruct',
  'mistral-large-latest': 'Mistral Large Latest',
  'mistral-medium': 'Mistral Medium',
  'mistral-medium-latest': 'Mistral Medium Latest',
  'mistral-small-latest': 'Mistral Small Latest',
  'mistral-tiny': 'Mistral Tiny',
  'mistralai/Mistral-7B-Instruct-v0.2': 'Mistral 7B Instruct v0.2',
  'mistralai/Mistral-7B-Instruct-v0.3': 'Mistral 7B Instruct v0.3',
  'mistralai/Mistral-7B-Instruct-v0.3_1': 'Mistral 7B Instruct v0.3',
  'mistralai/Mistral-Nemo-Instruct-2407': 'Mistral Nemo Instruct 2407',
  'mistralai/Mixtral-8x7B-Instruct-v0.1': 'Mixtral 8x7B Instruct v0.1',
  'mistralai/Pixtral-12B-2409': 'Pixtral 12B 2409',
  'mistralai/Pixtral-12B-2409_1': 'Pixtral 12B 2409',
  'nvidia/Llama-3.1-Nemotron-70B-Instruct-HF': 'Llama 3.1 Nemotron 70B Instruct HF',
  o1: 'O1',
  'o1-mini': 'O1 Mini',
  'o3-mini': 'O3 Mini',
  'o4-mini': 'O4 Mini',
  'openchat/openchat-3.5-1210': 'OpenChat 3.5 1210',
  'perplexity-ai/r1-1776': 'Perplexity R1 1776',
  'upstage/SOLAR-10.7B-Instruct-v1.0': 'Solar 10.7B Instruct v1.0',

  all_data: 'All Data',
  auto: 'Auto',
  auto_rag: 'Auto RAG',
  chat_session_rename: 'Chat Session Rename',
  hyde1: 'HyDE v1',
  hyde2: 'HyDE v2',
  llm_only: 'LLM Only',
  pii_model: 'PII Model',
  pii_presidio: 'PII Presidio',
  pii_regex: 'PII Regex',
  prompt_guardrails: 'Prompt Guardrails',
  rag: 'RAG',
  'rag+': 'RAG+',
  self_reflection: 'Self Reflection',

  apikeycreatedforuser: 'API Key Created for User',
  apikeydeletedforuser: 'API Key Deleted for User',
  apikeyexpiryset: 'API Key Expiry Set',
  apikeydeactivated: 'API Key Deactivated',

  userrolesupdated: 'User Roles Updated',
  rolecreated: 'Role Created',
  rolepermissionsupdated: 'Role Permissions Updated',
  roledeleted: 'Role Deleted',
  usergroupcreated: 'User Group Created',
  usergrouprolesreset: 'User Group Roles Reset',
  usergroupsdeleted: 'User Groups Deleted',
  userdeleted: 'User Deleted',
  archivedcollectiondeleted: 'Archived Collection Deleted',
  userjobcanceled: 'User Job Canceled',
};

export const mapActionName = (action: string): string => {
  if (!action) return 'Unknown';

  // Try exact match first
  if (keyNameMap[action]) {
    return keyNameMap[action];
  }

  // Try lowercase match
  const lowerAction = action.toLowerCase().replace(/\s+/g, '').replace(/_/g, '');
  if (keyNameMap[lowerAction]) {
    return keyNameMap[lowerAction];
  }

  // Return original if no mapping found
  return action;
};

export const mapGPTeKeys = (input: Record<string, number> | undefined): Record<string, number> => {
  const result: Record<string, number> = {};

  if (!input) return result;
  for (const [key, value] of Object.entries(input)) {
    const readableKey = mapActionName(key);
    result[readableKey] = Number(value);
  }

  return result;
};
